import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Add,
  Assignment,
  Flag,
  Folder,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import { useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Link, Task } from "@dhx/react-gantt";
import { FormDrawer } from "../../../shared/components";
import { ProjectGantt, ProjectGanttHandle } from "../components/ProjectGantt";
import {
  PhaseForm,
  PhaseFormActions,
  PhaseFormHandle,
  ActivityForm,
  ActivityFormActions,
  ActivityFormHandle,
  StageGateForm,
  StageGateFormActions,
  StageGateFormHandle,
} from "../components";
import { useScheduleEditor } from "../hooks";
import { transformSchedulingItemsToTasks } from "../utils/transformers";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useAssetDetails } from "../../projectOverview/hooks/useAssetDetails";

export const ScheduleAndPlanning: React.FC = () => {

  const { can } = useCapabilities();

  const isAllowedToEditActivity = can("operational_delivery:project:activity:edit");

  const isAllowedToDeleteActivity = can("operational_delivery:project:activity:delete");
  const isAllowedToDeletePhase = can("operational_delivery:project:phase:delete");
  const isAllowedToDeleteStageGate = can("operational_delivery:project:stage_gate:delete");

  const { projectId } = useParams<{ projectId: string }>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const phaseFormRef = useRef<PhaseFormHandle>(null);
  const activityFormRef = useRef<ActivityFormHandle>(null);
  const stageGateFormRef = useRef<StageGateFormHandle>(null);
  const ganttRef = useRef<ProjectGanttHandle>(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<string>("day");

  const { assetData } = useAssetDetails(projectId);

  const {
    stageGateId,
    schedules,
    links,
    isLoading,
    isError,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    openDrawer,
    editingScheduleItem,
    deleteConfirmOpen,
    phaseOptions,
    phaseFormData,
    activityFormData,
    stageGateFormData,
    handleDrawerClose,
    handleTaskDblClick,
    handleAddTaskFromGantt,
    handleTaskUpdate,
    handleProgressUpdate,
    handleTaskReorder,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSavePhase,
    handleSaveActivity,
    handleSaveStageGate,
    handleMenuItemClick,
    handleLinkAdd,
    handleLinkDelete,
  } = useScheduleEditor(Number(projectId));

  const tasks = useMemo(() => {
    if (!schedules.length) return [];
    return transformSchedulingItemsToTasks(schedules);
  }, [schedules]);

  const ganttLinks: Link[] = useMemo(() => {
    const apiTypeToGanttType: Record<string, string> = {
      finish_to_start: "0",
      start_to_start: "1",
      finish_to_finish: "2",
      start_to_finish: "3",
    };

    return links.map((link) => ({
      id: link.id,
      source: Number(link.source_id),
      target: Number(link.target_id),
      type: apiTypeToGanttType[link.dependency_type] || "0",
    }));
  }, [links]);

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClickWrapper = (type: "phase" | "activity" | "stageGate") => {
    handleMenuItemClick(type);
    handleMenuClose();
  };

  const handleZoomChange = (
    event: React.MouseEvent<HTMLElement>,
    newZoom: string | null
  ) => {
    if (newZoom !== null) {
      setZoomLevel(newZoom);
      ganttRef.current?.setZoom(newZoom);
    }
  };

  const handleToggleCollapse = () => {
    if (isCollapsed) {
      ganttRef.current?.expandAll();
    } else {
      ganttRef.current?.collapseAll();
    }
    setIsCollapsed(!isCollapsed);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load scheduling data.{" "}
          {error || "Please try again."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <ToggleButtonGroup
          value={zoomLevel}
          exclusive
          onChange={handleZoomChange}
          aria-label="zoom level"
          size="small"
          sx={{ mr: 2 }}
        >
          <ToggleButton value="day" aria-label="day view">
            Day
          </ToggleButton>
          <ToggleButton value="week" aria-label="week view">
            Week
          </ToggleButton>
          <ToggleButton value="month" aria-label="month view">
            Month
          </ToggleButton>
          <ToggleButton value="year" aria-label="year view">
            Year
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="text"
          startIcon={isCollapsed ? <UnfoldMore /> : <UnfoldLess />}
          onClick={handleToggleCollapse}
          sx={{
            color: "primary.main",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {isCollapsed ? "Expand All" : "Collapse All"}
        </Button>
        <Button
          variant="text"
          startIcon={<Add />}
          onClick={handleAddClick}
          disabled={isCreating || isUpdating || isDeleting}
          sx={{
            color: "primary.main",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Add
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={() => handleMenuItemClickWrapper("activity")}>
            <ListItemIcon>
              <Assignment fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Activity</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClickWrapper("stageGate")}>
            <ListItemIcon>
              <Flag fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Stage Gate</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClickWrapper("phase")}>
            <ListItemIcon>
              <Folder fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Phase</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <ProjectGantt
          ref={ganttRef}
          tasks={tasks}
          links={ganttLinks}
          onTaskDblClick={(task: Task) => {
            if (task.type === "task") {
              const isActionOwner = schedules.filter((schedule) => schedule.id === Number(task.id)).find((schedule) => schedule.type === "activity")?.is_action_owner;
              if (isActionOwner) {
                handleTaskDblClick(task);
              }
            }
            if (task.type === "project") {
              const isActionOwner = schedules.filter((schedule) => schedule.id === Number(task.id)).find((schedule) => schedule.type === "phase")?.is_action_owner;
              if (isActionOwner) {
                handleTaskDblClick(task);
              }
            }
            if (task.type === "milestone") {
              const isActionOwner = schedules.filter((schedule) => schedule.id === Number(task.id)).find((schedule) => schedule.type === "stage_gate")?.is_action_owner;
              if (isActionOwner) {
                handleTaskDblClick(task);
              }
            }
            if (task.type === "task" || task.type === "milestone") {
              const stageGate = schedules.filter((schedule) => schedule.parent_id === Number(task.parent)).find((schedule) => schedule.type === "stage_gate");
              if (!stageGate?.is_decision_approved) {
                handleTaskDblClick(task);
              }
            }
            if (task.type === "project") {
              const stageGate = schedules.filter((schedule) => schedule.parent_id === Number(task.id)).find((schedule) => schedule.type === "stage_gate");
              if (!stageGate?.is_decision_approved) {
                handleTaskDblClick(task);
              }
            }
          }}
          onAddTask={handleAddTaskFromGantt}
          onTaskUpdate={handleTaskUpdate}
          onProgressUpdate={handleProgressUpdate}
          onTaskReorder={handleTaskReorder}
          onLinkAdd={handleLinkAdd}
          onLinkDelete={handleLinkDelete}
        />
      </Box>

      <FormDrawer
        open={openDrawer === "phase"}
        onClose={handleDrawerClose}
        title={editingScheduleItem ? "Edit Phase" : "Add Phase"}
        actions={
          <PhaseFormActions
            onSave={() => phaseFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!editingScheduleItem}
            isDeleteAllowed={isAllowedToDeletePhase}
          />
        }
      >
        <PhaseForm
          ref={phaseFormRef}
          onSave={handleSavePhase}
          onCancel={handleDrawerClose}
          initialData={phaseFormData}
          isEditing={!!editingScheduleItem}
        />
      </FormDrawer>

      <FormDrawer
        open={openDrawer === "activity"}
        onClose={handleDrawerClose}
        title={editingScheduleItem ? "Edit Activity" : "Add Activity"}
        actions={
          <ActivityFormActions
            onSave={() => activityFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!editingScheduleItem}
            isDeleteAllowed={isAllowedToDeleteActivity}
          />
        }
      >
        <ActivityForm
          ref={activityFormRef}
          onSave={handleSaveActivity}
          onCancel={handleDrawerClose}
          initialData={activityFormData}
          phaseOptions={phaseOptions}
          stageGateId={stageGateId}
          activityId={editingScheduleItem?.id}
          isEditing={!!editingScheduleItem}
          isAllowedEdit={isAllowedToEditActivity}
          isActionOwner={isAllowedToEditActivity ? true : editingScheduleItem?.is_action_owner}
          assetData={assetData || []}
        />
      </FormDrawer>

      <FormDrawer
        open={openDrawer === "stageGate"}
        onClose={handleDrawerClose}
        title={
          editingScheduleItem || stageGateId
            ? "Edit Stage Gate"
            : "Add Stage Gate"
        }
        actions={
          <StageGateFormActions
            onSave={() => stageGateFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!stageGateId}
            isDeleteAllowed={isAllowedToDeleteStageGate}
          />
        }
      >
        <StageGateForm
          ref={stageGateFormRef}
          stageGateId={stageGateId}
          onSave={handleSaveStageGate}
          onCancel={handleDrawerClose}
          initialData={stageGateFormData}
          isEditing={!!editingScheduleItem}
          phaseOptions={phaseOptions}
          projectId={projectId}
        />
      </FormDrawer>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{editingScheduleItem?.name}"?
            {editingScheduleItem?.type === "phase" && (
              <Box
                component="span"
                sx={{ display: "block", mt: 1, fontWeight: "bold" }}
              >
                Warning: Deleting this phase may affect associated activities.
              </Box>
            )}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
